// SuperAdminLogin.jsx - Updated with Forgot Password Link
import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Button } from '../components/ui/button';
import { useToast } from '../hooks/use-toast';
import { useNavigate } from 'react-router-dom';
import { Shield, Lock, User, Eye, EyeOff } from 'lucide-react';
import { BASE_URL } from '../lib/api';

export default function SuperAdminLoginPage() {

  const { toast } = useToast();
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    twoFactorCode: ''
  });
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState(null);

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
    
    if (status) {
      setStatus(null);
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }
    
    if (!formData.password.trim()) {
      newErrors.password = 'Password is required';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    setStatus(null);

    try {
      const { email, password } = formData;
      
      console.log('SuperAdmin login attempt:', { email, hasPassword: !!password });

      const response = await fetch(`${BASE_URL}/authentication/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: email.trim(),
          password: password
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Authentication failed');
      }

      if (data.user && data.user.user_type === 'superadmin') {
        console.log('SuperAdmin authentication successful');
        
        localStorage.clear();
        
        localStorage.setItem('superadmin_token', data.token);
        localStorage.setItem('admin_user', JSON.stringify({
          id: data.user.id,
          username: data.user.fullName || 'SuperAdmin',
          email: data.user.email,
          role: 'SuperAdmin',
          userType: data.user.user_type,
          permissions: ['all'],
          loginTime: new Date().toISOString()
        }));

        setStatus({ type: 'success', message: `Welcome back, ${data.user.fullName || 'SuperAdmin'}!` });
          toast({
          title: 'Welcome back!',
          description: 'Redirecting to dashboard...',
        });
        setTimeout(() => {
          navigate('/dashboard')
        }, 500);

      } else {
        throw new Error('Access denied. Super admin privileges required.');
      }

    } catch (error) {
      console.error('SuperAdmin login error:', error);

      let errorMessage = 'Authentication failed. Please check your credentials.';
      
      if (error.message.includes('User does not exist')) {
        errorMessage = 'No account found with this email address.';
      } else if (error.message.includes('Invalid email or password')) {
        errorMessage = 'Invalid email or password.';
      } else if (error.message.includes('confirm your email')) {
        errorMessage = 'Please confirm your email address before logging in.';
      } else if (error.message.includes('Super admin privileges')) {
        errorMessage = 'Access denied. Super admin privileges required.';
      } else if (error.message.includes('Network Error') || error.name === 'TypeError') {
        errorMessage = 'Connection error. Please check your internet connection.';
      }

      setStatus({ type: 'error', message: errorMessage });

    } finally {
      setIsSubmitting(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !isSubmitting) {
      handleSubmit();
    }
  };

  return (
    <div className="flex min-h-screen w-full items-center justify-center animated-gradient-background p-4">
      <div className="w-full max-w-sm">
        {/* Logo */}
        <div className="mb-8 flex w-full justify-center">
          <img
            src="./evolvv.png"
            alt="evolvv Logo"
            width={200}
            height={50}
            className="dark:invert"
          />
        </div>

        <Card className="w-full shadow-xl bg-card/90 backdrop-blur-sm">
          <CardHeader className="space-y-1 text-center">
            <div className="flex justify-center mb-2">
              <div className="p-3 rounded-full bg-gradient-to-r from-purple-600 to-blue-600">
                <Shield className="h-6 w-6 text-white" />
              </div>
            </div>
            <CardTitle className="text-2xl text-card-foreground">Administrator Access</CardTitle>
            <CardDescription className="text-card-foreground/80">
              Enter admin credentials to access the dashboard
            </CardDescription>
          </CardHeader>

          <CardContent className="space-y-4">
            {/* Status Message */}
            {status && (
              <div className={`p-3 rounded-lg text-sm font-medium ${
                status.type === 'error' 
                  ? 'bg-destructive/10 text-destructive border border-destructive/20' 
                  : 'bg-green-50 text-green-700 border border-green-200'
              }`}>
                {status.message}
              </div>
            )}

            {/* Email Field */}
            <div className="space-y-2">
              <Label htmlFor="email" className="text-card-foreground/90">
                Email Address
              </Label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  id="email"
                  name="email"
                  type="email"
                  placeholder="superadmin@example.com"
                  value={formData.email}
                  onChange={handleInputChange}
                  onKeyPress={handleKeyPress}
                  className="pl-10 bg-background/80 text-foreground placeholder:text-muted-foreground/70"
                  disabled={isSubmitting}
                  autoComplete="email"
                />
              </div>
              {errors.email && (
                <p className="text-destructive text-sm">{errors.email}</p>
              )}
            </div>

            {/* Password Field */}
            <div className="space-y-2">
              <Label htmlFor="password" className="text-card-foreground/90">
                Password
              </Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  id="password"
                  name="password"
                  type={showPassword ? "text" : "password"}
                  placeholder="••••••••"
                  value={formData.password}
                  onChange={handleInputChange}
                  onKeyPress={handleKeyPress}
                  className="pl-10 bg-background/80 text-foreground placeholder:text-muted-foreground/70"
                  disabled={isSubmitting}
                  autoComplete="current-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                  disabled={isSubmitting}
                >
                  {showPassword ? (
                    <EyeOff className="h-4 w-4" />
                  ) : (
                    <Eye className="h-4 w-4" />
                  )}
                </button>
              </div>
              {errors.password && (
                <p className="text-destructive text-sm">{errors.password}</p>
              )}
            </div>

            {/* 2FA Code Field */}
            <div className="space-y-2">
              <Label htmlFor="twoFactorCode" className="text-card-foreground/90">
                2FA Code <span className="text-muted-foreground">(Optional)</span>
              </Label>
              <div className="relative">
                <Shield className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  id="twoFactorCode"
                  name="twoFactorCode"
                  type="text"
                  placeholder="Enter your 2FA code"
                  value={formData.twoFactorCode}
                  onChange={handleInputChange}
                  onKeyPress={handleKeyPress}
                  className="pl-10 bg-background/80 text-foreground placeholder:text-muted-foreground/70"
                  disabled={isSubmitting}
                  maxLength={6}
                  autoComplete="one-time-code"
                />
              </div>
            </div>

            <div className="flex items-center justify-end">
              <a 
                href="/forget-password" 
                className="text-sm text-blue-500 hover:text-blue-600 hover:underline transition-colors"
              >
                Forgot password?
              </a>
            </div>
          </CardContent>

          <CardFooter className="flex flex-col gap-4">
            <Button 
              type="button"
              onClick={handleSubmit} 
              className="w-full bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white font-semibold py-3 rounded-lg transition-all duration-300 transform hover:scale-105" 
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                  Authenticating...
                </div>
              ) : (
                'Login as Administrator'
              )}
            </Button>

            <p className="text-center text-sm text-card-foreground/80">
              Need help? Contact{' '}
              <a 
                href="mailto:support@evolvv.com" 
                className="font-semibold text-blue-500 hover:text-blue-600 hover:underline transition-colors"
              >
                System Administrator
              </a>
            </p>
          </CardFooter>
        </Card>

        {/* Security Notice */}
        <div className="mt-4 text-center">
          <p className="text-xs text-muted-foreground/60 flex items-center justify-center gap-1">
            <Shield className="h-3 w-3" />
            Secure admin portal protected by encryption
          </p>
        </div>
      </div>
    </div>
  );
}